import React, { useCallback, useContext, useMemo } from "react";
import { CheckboxField, HeaderB } from "components";
import lang from "translations";
import { Path } from "paths";
import { FragmentA } from "components/fragments";
import { useEffect, useState } from "react";
import { searchLocation } from "apis/location.api";

import {
  Field,
  Input,
  Panel,
  ActionButton,
  Toast,
  Form,
  Skeleton,
} from "components/commons";

import { ModuleWrapper } from "components/fragments";
import { useForm, useModal, useRouter, useMount } from "hooks";
import { VenueContext } from "contexts";
import { mixpanel, TrackEvent } from "mixpanel";

import initialFormState from "./resource.form-state";
import { Select } from "components";
import { useApi } from "hooks";
import { searchResourceType } from "apis/resource-type.api";
import { searchResourceStatus } from "apis/resource.api";
import { resourceType } from "mappers/resource-type.mapper";
import { resourceStatus } from "mappers/resource-status.mapper";

const ResourceForm = ({
  submit,
  title,
  submitting,
  initialState,
  deleteResourceButton,
  loading,
  error,
}) => {
  const { venue } = useContext(VenueContext);
  const { history } = useRouter();
  const { venueId } = venue;
  const [locationOptions, setLocationOptions] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  
useEffect(() => {
        async function fetchLocations() {
            setLoadingLocations(true);
            try {
                // Fetch all locations (no filter, or add venueId if needed)
                const res = await searchLocation({
                    venueId,
                    page: 1,
                    pageSize: 200,
                });
                // Map locations to options (text, value) as in supply item form
                const options = (res.data || [])
                    .filter(loc => (loc.locationId || loc.id) && (loc.locationName || loc.name))
                    .map(loc => ({
                        text: String(loc.locationName || loc.name),
                        value: String(loc.locationId || loc.id),
                    }));
                setLocationOptions(options);
            } catch (e) {
                setLocationOptions([]);
            } finally {
                setLoadingLocations(false);
            }
        }
        fetchLocations();
    }, [venueId]);



  const formState = useMemo(() => {
    return initialFormState(initialState);
  }, [initialState]);

  const unsaveChangesModal = useModal();

  const {
    fields,
    modifyField,
    submitForm,
    getFormValues,
    applyFieldErrors,
    dirty,
  } = useForm({
    initialState: formState,
  });

  /** Navigate Back */
  const goToList = useCallback(() => {
    history.push(Path.RESOURCE);
  }, [history]);

  /** Leave Page */
  const leavePage = useCallback(() => {
    if (dirty) {
      unsaveChangesModal.show({
        ok: () => {
          goToList();
          unsaveChangesModal.close();
        },
      });
      return;
    }
    goToList();
  }, [dirty, unsaveChangesModal, goToList]);

  /** Submit Handler */
  const handleSubmit = useCallback(async () => {
    const params = getFormValues();
    const obj = {
      defaultName: params.defaultName,
      customName: params.customName,
      capacity: params.capacity,
      externalResourceId: params.externalResourceId,
      allowOnline: params.allowOnline,
      allowWalkIn: params.allowWalkIn,
      resourceTypeId: params.resourceTypeId,
      resourceStatusId: params.resourceStatusId,
      locationId: params.locationId,
    };

    try {
      mixpanel.track(TrackEvent.Clickedbutton, {
        Button: lang.saveResourceForm,
        Page: title,
      });

      const res = await submit({ ...obj, venueId });

      Toast({
        content: res.message,
        success: true,
        icon: "check",
      }).open();

      // Only redirect if editing (not creating)
      if (title === lang.editResource) {
        history.push(Path.RESOURCE);
      }
    } catch ({ code, handleError }) {
      if (code === 3003) {
        applyFieldErrors({
          defaultName: lang.resourceNameAlreadyExist,
        });
      } else {
        handleError();
      }
    }
  }, [submit, getFormValues, venueId, applyFieldErrors, title, history]);

  /** Submit Trigger */
  const submitFormValue = () => {
    submitForm(handleSubmit);
  };

  // Resource Type Dropdown
  const {
    request: getResourceTypeOptions,
    mappedData: mappedResourceTypes = [],
    loading: resourceTypeLoading,
  } = useApi({
    api: searchResourceType,
    isArray: true,
    mapper: resourceType,
    params: { venueId },
  });

  const resourceTypeOptions = useMemo(() => {
    return mappedResourceTypes.map((type) => {
      return { text: `${type.name} - (${type.shape})`, value: type.id };
    });
  }, [mappedResourceTypes]);



  // Resource Status Dropdown
  const {
    request: getResourceStatusOptions,
    mappedData: mappedResourceStatuses = [],
    loading: resourceStatusLoading,
  } = useApi({
    api: searchResourceStatus,
    isArray: true,
    mapper: resourceStatus,
    params: { venueId },
  });

  const resourceStatusOptions = useMemo(() => {
    return mappedResourceStatuses.map((status) => {
      return { text: `${status.code} (${status.description})`, value: status.resourceStatusId };
    });
  }, [mappedResourceStatuses]);

  useMount(() => {
    getResourceTypeOptions({ venueId });
    getResourceStatusOptions({ venueId });
  });

  return (
    <ModuleWrapper
      error={error}
      header={
        <HeaderB title={title} returnText={lang.resources} onClick={leavePage} />
      }
    >
      <Form
        unsaveChangesModal={unsaveChangesModal}
        onSubmit={submitFormValue}
        isPrompt={dirty}
      >
        <FragmentA title={lang.basicInfo}>
          <Panel>
            {loading ? (
              <Skeleton />
            ) : (
              <div>
                {/* Default Name */}
                <Field label={fields.defaultName.label} {...fields.defaultName}>
                  <Input required {...fields.defaultName} onChange={modifyField} />
                </Field>

                {/* Custom Name */}
                <Field label={fields.customName.label} {...fields.customName}>
                  <Input {...fields.customName} onChange={modifyField} />
                </Field>

                <Field label={fields.capacity.label} {...fields.capacity}>
                  <Input
                    required
                    {...fields.capacity}
                    onChange={(name, { value }) => {
                      if (!/^\d*$/.test(value)) return;
                      modifyField(name, { value });
                    }}
                  />
                </Field>

                <Field label={fields.externalResourceId.label} {...fields.externalResourceId}>
                  <Input {...fields.externalResourceId} onChange={modifyField} />
                </Field>

                <Field label={fields.allowOnline.label} {...fields.allowOnline}>
                  <CheckboxField {...fields.allowOnline} onChange={modifyField} />
                </Field>

                <Field label={fields.allowWalkIn.label} {...fields.allowWalkIn}>
                  <CheckboxField {...fields.allowWalkIn} onChange={modifyField} />
                </Field>

                <Field label={fields.locationId.label} {...fields.locationId}>
                  <Select
                    {...fields.locationId}
                    options={locationOptions}
                    onChange={modifyField}
                    loading={loadingLocations}
                  />
                </Field>

                <Field label={fields.resourceTypeId.label} {...fields.resourceTypeId}>
                  <Select
                    {...fields.resourceTypeId}
                    options={resourceTypeOptions}
                    loading={resourceTypeLoading}
                    onChange={modifyField}
                    placeholder={lang.pleaseSelectResourceType || "Please select resource type"}
                  />
                </Field>

                <Field label={fields.resourceStatusId.label} {...fields.resourceStatusId}>
                  <Select
                    {...fields.resourceStatusId}
                    options={resourceStatusOptions}
                    loading={resourceStatusLoading}
                    onChange={modifyField}
                    placeholder={lang.pleaseSelectStatus || "Please select status"}
                  />
                </Field>
              </div>
            )}
          </Panel>
        </FragmentA>

        {/* Buttons */}
        <ActionButton
          showLine
          loading={submitting}
          primary={{
            onClick: () => submitFormValue(),
          }}
          secondary={{
            onClick: () => leavePage(),
          }}
          danger={deleteResourceButton}
        />
      </Form>
    </ModuleWrapper>
  );
};

export default ResourceForm;
